import {useCallback, useEffect, useState} from 'react';
import {getAuth} from 'firebase/auth';

export default function useVodPlaybackSource({apiBase, teamId, mapId, videoId, videoData, getSessionId}) {
  const present = !!videoData;
  const status = videoData?.status;
  const storagePath = videoData?.storagePath || '';
  const videoURL = videoData?.videoURL || '';
  const key = JSON.stringify([apiBase, teamId, mapId, videoId, present, status, storagePath, videoURL]);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({key: '', src: '', error: '', loading: false});
  const retry = useCallback(() => setAttempt(value => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const update = values => {if (!cancelled) setState({key, src: '', error: '', loading: false, ...values});};
    async function resolve() {
      if (!present || status === 'processing' || status === 'failed') {update({}); return;}
      if (!storagePath) {update({src: videoURL}); return;}
      update({loading: true});
      try {
        const token = await getAuth().currentUser?.getIdToken?.();
        if (cancelled) return;
        const path = '/' + storagePath.replace(/^\/+/, '');
        const response = await fetch(`${apiBase}/signed-url?path=${encodeURIComponent(path)}&sessionId=${encodeURIComponent(getSessionId())}&mapId=${encodeURIComponent(mapId)}&videoId=${encodeURIComponent(videoId)}`, {
          signal: controller.signal, headers: token ? {Authorization: `Bearer ${token}`} : {},
        });
        const result = await response.json();
        if (!response.ok) throw Error(result?.message || `Video access failed (HTTP ${response.status}).`);
        if (typeof result.url !== 'string' || !result.url) throw Error('The video playback address is unavailable. Please retry.');
        update({src: result.url});
      } catch (error) {
        if (cancelled || error.name === 'AbortError') return;
        update({error: error.message || 'Could not load this video. Please retry.'});
      }
    }
    void resolve();
    return () => {cancelled = true; controller.abort();};
    // Review metadata such as viewers, ratings and notes must not replace src.
  }, [apiBase, teamId, mapId, videoId, present, status, storagePath, videoURL, key, getSessionId, attempt]);

  return {...(state.key === key ? state : {src: '', error: '', loading: present}), retry};
}
